import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, isAuthenticated } from '../App';
import { Briefcase, Users, BarChart3, Building2, CheckCircle, ArrowRight, Shield, Clock, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const EmployerHome = () => {
  const navigate = useNavigate();
  const [footerConfig, setFooterConfig] = useState(null);
  const [stats, setStats] = useState({ jobs: 0, candidates: 0, applications: 0 });

  useEffect(() => {
    // If already logged in as admin/crm, redirect to dashboard
    if (isAuthenticated()) {
      const token = localStorage.getItem('token');
      try {
        const decoded = JSON.parse(atob(token.split('.')[1]));
        if (decoded.role !== 'candidate') {
          navigate('/dashboard');
          return;
        }
      } catch {
        // Continue to show employer page
      }
    }
    
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const footerRes = await axios.get(`${API}/footer-config`);
      setFooterConfig(footerRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  const features = [
    {
      icon: <Briefcase className="h-8 w-8" />,
      title: "Post Jobs Easily",
      description: "Create and publish job listings in minutes with our intuitive job posting system"
    },
    {
      icon: <Users className="h-8 w-8" />,
      title: "Manage Candidates",
      description: "Track applications, schedule interviews, and manage your hiring pipeline"
    },
    {
      icon: <BarChart3 className="h-8 w-8" />,
      title: "Analytics Dashboard",
      description: "Get insights on job performance, application rates, and hiring metrics"
    },
    {
      icon: <Shield className="h-8 w-8" />,
      title: "Verified Candidates",
      description: "Access pre-screened and verified candidate profiles"
    }
  ];

  const benefits = [
    "Unlimited job postings",
    "Applicant tracking system",
    "Email automation",
    "Team collaboration",
    "Analytics & reports",
    "Priority support"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F4C81] to-[#051C31] flex flex-col">
      {/* Header */}
      <header className="bg-transparent border-b border-white/10 sticky top-0 z-50 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-3">
            <img 
              src="/logo.png?v=15" 
              alt="Logo" 
              className="h-8 w-8 md:h-10 md:w-10 rounded-lg shadow-lg"
            />
            <div className="flex items-center gap-2">
              <span className="font-bold text-lg md:text-xl text-white">Officium Connect</span>
              <span className="text-xs bg-[#10B981]/30 text-purple-300 px-2 py-0.5 rounded-full hidden sm:inline">Employer</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2 md:gap-4">
            <Link to="/home" className="text-white/70 hover:text-white text-xs md:text-sm hidden sm:block">
              For Candidates
            </Link>
            <Link to="/login">
              <Button className="bg-white text-purple-900 hover:bg-purple-100 font-semibold text-sm px-3 md:px-4">
                Sign In
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(16,185,129,0.15)_0%,transparent_50%)]"></div>
        
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-[#10B981]/20 text-[#10B981] px-4 py-2 rounded-full text-sm mb-6">
              <TrendingUp className="h-4 w-4" />
              Trusted by 500+ companies
            </div>
            
            <h1 className="font-heading text-4xl md:text-6xl font-bold text-white mb-6 leading-tight tracking-tight">
              Hire the Best Talent
              <span className="block text-[#10B981]">
                Faster & Smarter
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
              Streamline your recruitment process with our powerful CRM. Post jobs, track candidates, and make data-driven hiring decisions.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/login">
                <Button size="lg" className="bg-[#10B981] hover:bg-[#0A7D55] text-white font-semibold px-8 rounded-full">
                  Get Started Free
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-full">
                  Contact Sales
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white/5 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4 tracking-tight">
              Everything You Need to Hire
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Powerful tools to streamline your recruitment workflow
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, idx) => (
              <Card key={idx} className="bg-white/10 border-white/10 backdrop-blur-lg hover:bg-white/15 transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-6">
                  <div className="h-14 w-14 rounded-2xl bg-[#10B981]/20 flex items-center justify-center text-[#10B981] mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="font-semibold text-lg text-white mb-2">{feature.title}</h3>
                  <p className="text-white/60 text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Why Choose Officium Connect?
              </h2>
              <p className="text-white/60 mb-8">
                Join hundreds of companies that trust us for their hiring needs. Our platform is designed to make recruitment efficient and effective.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
                    <span className="text-white/80 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-8">
                <Link to="/login">
                  <Button className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white">
                    Start Hiring Today
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-3xl p-8 border border-white/10">
                <div className="space-y-4">
                  <div className="bg-white/10 rounded-xl p-4 flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                      <Users className="h-6 w-6 text-green-400" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">1000+</p>
                      <p className="text-white/60 text-sm">Active Candidates</p>
                    </div>
                  </div>
                  
                  <div className="bg-white/10 rounded-xl p-4 flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                      <Briefcase className="h-6 w-6 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">500+</p>
                      <p className="text-white/60 text-sm">Jobs Posted</p>
                    </div>
                  </div>
                  
                  <div className="bg-white/10 rounded-xl p-4 flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-[#10B981]/20 flex items-center justify-center">
                      <Clock className="h-6 w-6 text-[#10B981]" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">48 hrs</p>
                      <p className="text-white/60 text-sm">Avg. Time to First Applicant</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#10B981] to-[#0A7D55]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Transform Your Hiring?
          </h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">
            Join hundreds of companies already using Officium Connect to find and hire top talent.
          </p>
          <Link to="/login">
            <Button size="lg" className="bg-white text-[#0F4C81] hover:bg-purple-50 font-semibold px-8">
              Get Started Free
              <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      {footerConfig && (
        <footer className="bg-slate-900 border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 py-10">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="h-6 w-6 text-[#10B981]" />
                  <span className="font-bold text-lg text-white">{footerConfig.company_name || 'Officium Connect'}</span>
                </div>
                <p className="text-white/50 text-sm">
                  {footerConfig.company_tagline || "India's trusted platform for verified careers - Remote & Office."}
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-white text-sm uppercase tracking-wider mb-3">COMPANY</h4>
                <ul className="space-y-2">
                  {(footerConfig.company_links || []).filter(l => l.is_active !== false).map((link, idx) => (
                    <li key={idx}>
                      <Link to={link.url} className="text-white/50 hover:text-[#10B981] text-sm">{link.label}</Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-white text-sm uppercase tracking-wider mb-3">SUPPORT</h4>
                <ul className="space-y-2">
                  {(footerConfig.support_links || []).filter(l => l.is_active !== false).map((link, idx) => (
                    <li key={idx}>
                      <Link to={link.url} className="text-white/50 hover:text-[#10B981] text-sm">{link.label}</Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-white text-sm uppercase tracking-wider mb-3">LEGAL</h4>
                <ul className="space-y-2">
                  {(footerConfig.legal_links || []).filter(l => l.is_active !== false).map((link, idx) => (
                    <li key={idx}>
                      <Link to={link.url} className="text-white/50 hover:text-[#10B981] text-sm">{link.label}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="mt-8 pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="text-white/40 text-sm">
                <p>{footerConfig.copyright_text || `© ${new Date().getFullYear()} Officium Connect. All rights reserved.`}</p>
              </div>
              <div className="flex gap-4">
                <Link to="/home" className="text-white/40 hover:text-[#10B981] text-sm">
                  For Candidates →
                </Link>
              </div>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
};

export default EmployerHome;
